import { FieldSettings, ProcessListsSettings } from "./SettingsFields";
import { ChecklistSettings } from "@/ui/checklists";
import { PortalSettings } from "@/ui/portal";
import { AiUsagePanel } from "@/ui/ai-usage";
import { ImportSettings } from "./SettingsImport";
import { LawyerTribunalSettings } from "./SettingsLawyers";
import { MailSettings } from "./MailPages";
import { BackupSettings } from "./SettingsBackups";
import { MasterSheets, ScoringSettings } from "./SettingsScoring";
import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { confirmar } from "@/ui/feedback";
import { api, qs } from "@/lib/api";
import { navigate, useSearch, useTitle } from "@/lib/router";
import { ACTION_LABEL, fmtDate, fmtDateTime } from "@/lib/format";
import { useCatalog, useLookups, type Catalog } from "@/lib/catalog";
import { DocumentSettings } from "./SettingsDocuments";
import { TelegramAdmin } from "@/ui/telegram";
import { EscavadorSettings } from "@/ui/escavador";
import { IntegrationSettings, LgpdSettings, PlatformSettings } from "./SettingsPlatform";
import { FeesSettings } from "./SettingsFees";
import { applyTheme, DEFAULT_THEME, FONTS, type OfficeFont, type OfficeTheme } from "@/ui/theme";
import { AvatarEditor, BrandSettings } from "@/ui/branding";
import { Avatar } from "@/ui/icons";
import {
  AreaSelect, Badge, Card, Combobox, DataTable, ReasonDialog, EntityPicker, ErrorBox, Field, FormGrid, Loading, Modal, PageHeader, Tabs, UnitSelect,
  UserSelect, useAct, type EntityRef,
  GroupedTabs,
} from "@/ui/kit";

type Tab = "campos" | "usuarios" | "unidades" | "areas" | "listas" | "tipos" | "etapas" | "checklists" | "oabs" | "feriados" | "automacoes" | "ia" | "papeis" | "documentos"
  | "integracoes" | "honorarios" | "api" | "seguranca" | "aparencia" | "auditoria" | "importacao" | "advogados" | "pontuacao" | "cadastros" | "emails" | "backups" | "portal";
const CAT = [["catalogo"]];

function GoTo({ to }: { to: string }): null { useEffect(() => navigate(to, { replace: true }), [to]); return null; }

/** Nomes antigos de abas (links salvos) → aba nova. */
const ALIASES: Record<string, Tab> = { regras: "automacoes", automacao: "automacoes" };

/** Índice da busca: aba → palavras que levam até ela. */
const INDEX: Array<{ tab: Tab; label: string; words: string }> = [
  { tab: "aparencia", label: "Escritório › Dados e aparência", words: "logo cores tema fonte nome do escritório marca favicon login" },
  { tab: "unidades", label: "Escritório › Unidades", words: "unidade filial sociedade cnpj razão social áreas atendidas" },
  { tab: "pontuacao", label: "Escritório › Pontuação", words: "pontos ranking pontuação produtividade peso" },
  { tab: "usuarios", label: "Equipe e acesso › Usuários", words: "usuário colaborador convite e-mail permissão avulsa supervisor estagiário desativar senha" },
  { tab: "papeis", label: "Equipe e acesso › Papéis", words: "papel perfil permissões advogado sócio administrador" },
  { tab: "seguranca", label: "Equipe e acesso › Segurança e LGPD", words: "2fa autenticador sessões lgpd anonimizar exportar dados retenção login tentativas" },
  { tab: "auditoria", label: "Equipe e acesso › Auditoria", words: "auditoria histórico quem alterou log" },
  { tab: "areas", label: "Cadastros jurídicos › Áreas e tipos de ação", words: "área tipo de ação assunto família previdenciário trabalhista cível" },
  { tab: "listas", label: "Cadastros jurídicos › Listas do processo", words: "fase situação instância sistema pje eproc projudi grau juizado turma recursal" },
  { tab: "etapas", label: "Cadastros jurídicos › Etapas (kanban)", words: "etapa kanban quadro coluna caso processo" },
  { tab: "tipos", label: "Cadastros jurídicos › Tipos de atividade", words: "tipo de atividade tarefa petição audiência prazo protocolo evidência revisão risco" },
  { tab: "checklists", label: "Cadastros jurídicos › Checklists", words: "checklist lista de verificação itens" },
  { tab: "campos", label: "Cadastros jurídicos › Campos dos formulários", words: "campo obrigatório opcional oculto formulário cadastro" },
  { tab: "cadastros", label: "Cadastros jurídicos › Planilha mestre", words: "planilha mestre excel em massa importar cadastros baixar enviar" },
  { tab: "feriados", label: "Prazos e captura › Feriados e prazos", words: "feriado recesso suspensão prazo interno dias úteis" },
  { tab: "oabs", label: "Prazos e captura › OABs e portais", words: "oab captura djen intimação portal tribunal link" },
  { tab: "advogados", label: "Prazos e captura › Advogados e tribunais", words: "certificado a1 senha tribunal mni pje credencial advogado" },
  { tab: "documentos", label: "Documentos e honorários › Modelos de documentos", words: "modelo documento contrato procuração timbrado gerador word" },
  { tab: "honorarios", label: "Documentos e honorários › Honorários", words: "honorários tabela oab seccional valores" },
  { tab: "automacoes", label: "Automação e IA › Automações", words: "automação regra evento gatilho notificação" },
  { tab: "ia", label: "Automação e IA › IA", words: "ia inteligência artificial resumo assistente custo tokens limite preço anthropic" },
  { tab: "emails", label: "Integrações › E-mails (Microsoft 365)", words: "e-mail caixa push outlook microsoft 365 graph" },
  { tab: "integracoes", label: "Integrações › Serviços", words: "sharepoint telegram whatsapp clicksign escavador assinatura" },
  { tab: "portal", label: "Integrações › Portal do cliente", words: "portal cliente acesso" },
  { tab: "api", label: "Integrações › Funções e API", words: "api chave webhook funções liberar" },
  { tab: "backups", label: "Integrações › Backups", words: "backup cópia s3 restauração" },
  { tab: "importacao", label: "Migração › Importação do ADVBOX", words: "advbox importar migração planilha de-para" },
];
const fold = (x: string) => x.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();

function SettingsSearch({ allowed, onPick }: { allowed: (t: Tab) => boolean; onPick: (t: Tab) => void }): JSX.Element {
  const [q, setQ] = useState("");
  const words = fold(q).split(/\s+/).filter((w) => w.length >= 2);
  const hits = words.length ? INDEX.filter((e) => allowed(e.tab) && words.every((w) => fold(`${e.label} ${e.words}`).includes(w))).slice(0, 8) : [];
  return (
    <div className="settings-search">
      <input className="input" placeholder="Buscar configuração (ex.: instância, etapa, feriado, 2FA)…" value={q} onChange={(e) => setQ(e.target.value)}
        onKeyDown={(e) => { if (e.key === "Enter" && hits[0]) { onPick(hits[0].tab); setQ(""); } if (e.key === "Escape") setQ(""); }} />
      {q && (
        <div className="settings-search-hits">
          {hits.length ? hits.map((h) => <button key={h.tab} className="link-btn" onClick={() => { onPick(h.tab); setQ(""); }}>{h.label}</button>) : <span className="muted small">Nada encontrado.</span>}
        </div>
      )}
    </div>
  );
}

export function SettingsPage(): JSX.Element {
  useTitle("Configurações");
  const [search, set] = useSearch();
  const { can } = useLookups();
  const raw = search.get("aba") ?? "";
  if (ALIASES[raw]) return <GoTo to={`/configuracoes?aba=${ALIASES[raw]}`} />;
  // "Listas antigas" (formato antigo dos fluxos) continua no BPM / Workflow.
  if (raw === "fluxos") return <GoTo to="/fluxos?aba=fluxos" />;
  const tab = (raw as Tab) || (can("permissao.gerenciar") ? "usuarios" : can("config.gerenciar") ? "unidades" : "auditoria");
  const cfg = !can("config.gerenciar"), perm = !can("permissao.gerenciar");
  const allowed = (t: Tab) => (t === "usuarios" || t === "papeis" ? !perm : t === "auditoria" ? can("auditoria.ver") : !cfg);
  return (
    <div className="stack gap-16">
      <PageHeader title="Configurações" subtitle="Escritório, equipe, cadastros, prazos, documentos, automação e integrações" actions={<SettingsSearch allowed={allowed} onPick={(t) => set({ aba: t })} />} />
      <GroupedTabs<Tab> value={tab} onChange={(t) => set({ aba: t })} groups={[
        { key: "escritorio", label: "Escritório", tabs: [
          { key: "aparencia", label: "Dados e aparência", hidden: cfg },
          { key: "unidades", label: "Unidades", hidden: cfg },
          { key: "pontuacao", label: "Pontuação", hidden: cfg },
        ] },
        { key: "equipe", label: "Equipe e acesso", tabs: [
          { key: "usuarios", label: "Usuários", hidden: perm },
          { key: "papeis", label: "Papéis", hidden: perm },
          { key: "seguranca", label: "Segurança e LGPD", hidden: cfg },
          { key: "auditoria", label: "Auditoria", hidden: !can("auditoria.ver") },
        ] },
        { key: "juridico", label: "Cadastros jurídicos", tabs: [
          { key: "areas", label: "Áreas e tipos de ação", hidden: cfg },
          { key: "listas", label: "Listas do processo", hidden: cfg },
          { key: "etapas", label: "Etapas (kanban)", hidden: cfg },
          { key: "tipos", label: "Tipos de atividade", hidden: cfg },
          { key: "checklists", label: "Checklists", hidden: cfg },
          { key: "campos", label: "Campos dos formulários", hidden: cfg },
          { key: "cadastros", label: "Planilha mestre", hidden: cfg },
        ] },
        { key: "prazos", label: "Prazos e captura", tabs: [
          { key: "feriados", label: "Feriados e prazos", hidden: cfg },
          { key: "oabs", label: "OABs e portais", hidden: cfg },
          { key: "advogados", label: "Advogados e tribunais", hidden: cfg },
        ] },
        { key: "documentos", label: "Documentos e honorários", tabs: [
          { key: "documentos", label: "Modelos de documentos", hidden: cfg },
          { key: "honorarios", label: "Honorários", hidden: cfg },
        ] },
        { key: "automacao", label: "Automação e IA", tabs: [
          { key: "automacoes", label: "Automações", hidden: cfg },
          { key: "ia", label: "IA", hidden: cfg },
        ] },
        { key: "integracoes", label: "Integrações", tabs: [
          { key: "emails", label: "E-mails (Microsoft 365)", hidden: cfg },
          { key: "integracoes", label: "Serviços", hidden: cfg },
          { key: "portal", label: "Portal do cliente", hidden: cfg },
          { key: "api", label: "Funções e API", hidden: cfg },
          { key: "backups", label: "Backups", hidden: cfg },
        ] },
        { key: "migracao", label: "Migração", tabs: [
          { key: "importacao", label: "Importação do ADVBOX", hidden: cfg },
        ] },
      ]} />
      {tab === "areas" && <AreasTab />}
      {tab === "listas" && <ProcessListsSettings />}
      {tab === "etapas" && <Stages />}
      {tab === "tipos" && <ActivityTypes />}
      {tab === "checklists" && <ChecklistSettings />}
      {tab === "automacoes" && <Rules />}
      {tab === "ia" && <AiSettings />}
      {tab === "usuarios" && <Users />}
      {tab === "campos" && <FieldSettings />}
      {tab === "papeis" && <Roles />}
      {tab === "unidades" && <Units />}
      {tab === "oabs" && <div className="stack gap-16"><Oabs /><Portals /></div>}
      {tab === "feriados" && <div className="stack gap-16"><DeadlineSettings /><Holidays /></div>}
      {tab === "documentos" && <DocumentSettings />}
      {tab === "honorarios" && <FeesSettings />}
      {tab === "integracoes" && <div className="stack gap-16"><SharePointSettings /><TelegramAdmin /><EscavadorSettings /><IntegrationSettings /></div>}
      {tab === "api" && <PlatformSettings />}
      {tab === "importacao" && <ImportSettings />}
      {tab === "cadastros" && <MasterSheets />}
      {tab === "pontuacao" && <ScoringSettings />}
      {tab === "advogados" && <LawyerTribunalSettings />}
      {tab === "emails" && <MailSettings />}
      {tab === "backups" && <BackupSettings />}
      {tab === "portal" && <PortalSettings />}
      {tab === "seguranca" && <div className="stack gap-16"><SecuritySettings /><LgpdSettings /></div>}
      {tab === "aparencia" && <Appearance />}
      {tab === "auditoria" && <Audit />}
    </div>
  );
}

// ================================================================ usuários

interface UserRow {
  id: string; identityId: string; displayName: string; email: string; unitId: string; active: boolean; mfaEnabled: boolean; lastLoginAt: string | null; supervisorUserId?: string | null;
  roleAssignments: Array<{ id: string; scopeType: string; unitId: string | null; areaId: string | null; caseId: string | null; processId: string | null; role: { key: string; name: string } }>;
  permissionGrants: Array<{ id: string; permission: string; scopeType: string; unitId: string | null; areaId: string | null; caseId: string | null; processId: string | null }>;
}

function scopeLabel(s: { scopeType: string; unitId: string | null; areaId: string | null }, unitName: (id?: string | null) => string, areaName: (id?: string | null) => string): string {
  switch (s.scopeType) {
    case "global": return "todo o escritório";
    case "unit": return `unidade ${unitName(s.unitId)}`;
    case "area": return `área ${areaName(s.areaId)}${s.unitId ? ` em ${unitName(s.unitId)}` : ""}`;
    case "case": return "um caso";
    case "process": return "um processo";
    default: return s.scopeType;
  }
}

function Users(): JSX.Element {
  const { unitName, areaName } = useLookups();
  const [dlg, setDlg] = useState<null | "new" | { user: UserRow; mode: "role" | "perm" | "edit" }>(null);
  const inv = [["config-usuarios"], ["catalogo"]];
  const { data, error } = useQuery({ queryKey: ["config-usuarios"], queryFn: () => api.get<UserRow[]>("/config/usuarios") });
  const revokeRole = useAct((id: string) => api.post(`/config/papeis-atribuidos/${id}/revogar`, {}), inv);
  const revokePerm = useAct((id: string) => api.post(`/config/permissoes-concedidas/${id}/revogar`, {}), inv);
  const invite = useAct((id: string) => api.post(`/config/usuarios/${id}/convite`), []);
  return (
    <Card title="Equipe" actions={<button className="btn small primary" onClick={() => setDlg("new")}>Novo usuário</button>}>
      <ErrorBox error={error ?? revokeRole.error ?? revokePerm.error ?? invite.error} />
      {invite.isSuccess && <div className="success">E-mail para definir senha reenviado.</div>}
      <DataTable rows={data} rowKey={(u) => u.id} rowClass={(u) => (u.active ? "" : "row-muted")}
        columns={[
          { key: "n", label: "Pessoa", render: (u) => <div className="row nowrap" style={{ gap: 10 }}><Avatar name={u.displayName} id={u.id} size={30} /><div>{u.displayName}<div className="muted small">{u.email}</div></div></div> },
          { key: "u", label: "Lotação", render: (u) => unitName(u.unitId) },
          { key: "r", label: "Papéis e permissões", render: (u) => (
            <div className="stack" style={{ gap: 4 }}>
              {u.roleAssignments.map((r) => (
                <div key={r.id} className="row small"><Badge tone="info">{r.role.name}</Badge> <span className="muted">{scopeLabel(r, unitName, areaName)}</span>
                  <button className="link-btn small" onClick={() => revokeRole.mutate(r.id)}>revogar</button></div>
              ))}
              {u.permissionGrants.map((g) => (
                <div key={g.id} className="row small"><Badge tone="warn">{ACTION_LABEL[g.permission] ?? g.permission}</Badge> <span className="muted">{scopeLabel(g, unitName, areaName)}</span>
                  <button className="link-btn small" onClick={() => revokePerm.mutate(g.id)}>revogar</button></div>
              ))}
            </div>
          ) },
          { key: "s", label: "Segurança", width: 150, render: (u) => <>{u.mfaEnabled ? <Badge tone="ok">2FA ativo</Badge> : <Badge tone="warn">sem 2FA</Badge>}<div className="muted small">último acesso {u.lastLoginAt ? fmtDateTime(u.lastLoginAt) : "nunca"}</div></> },
          { key: "x", label: "", width: 190, render: (u) => (
            <div className="stack" style={{ gap: 4 }}>
              <div className="row"><button className="btn small" onClick={() => setDlg({ user: u, mode: "role" })}>+ papel</button><button className="btn small" onClick={() => setDlg({ user: u, mode: "perm" })}>+ permissão</button></div>
              <div className="row"><button className="btn small" onClick={() => setDlg({ user: u, mode: "edit" })}>Editar</button>{!u.lastLoginAt && <button className="btn small" onClick={() => invite.mutate(u.id)}>Reenviar convite</button>}</div>
            </div>
          ) },
        ]} />
      {dlg === "new" && <NewUserModal onClose={() => setDlg(null)} />}
      {dlg && typeof dlg === "object" && dlg.mode === "edit" && <EditUserModal user={dlg.user} onClose={() => setDlg(null)} />}
      {dlg && typeof dlg === "object" && dlg.mode !== "edit" && <GrantModal user={dlg.user} mode={dlg.mode} onClose={() => setDlg(null)} />}
    </Card>
  );
}

function ScopeFields({ s, set }: { s: ScopeState; set: (s: ScopeState) => void }): JSX.Element {
  return (
    <>
      <Field label="Onde vale">
        <select className="input" value={s.scopeType} onChange={(e) => set({ ...s, scopeType: e.target.value as ScopeState["scopeType"] })}>
          <option value="global">Todo o escritório</option><option value="unit">Uma unidade</option><option value="area">Uma área</option><option value="case">Um caso</option><option value="process">Um processo</option>
        </select>
      </Field>
      {(s.scopeType === "unit" || s.scopeType === "area") && <Field label="Unidade" required={s.scopeType === "unit"}><UnitSelect value={s.unitId} onChange={(v) => set({ ...s, unitId: v })} allowEmpty={s.scopeType === "area"} /></Field>}
      {s.scopeType === "area" && <Field label="Área" required><AreaSelect value={s.areaId} onChange={(v) => set({ ...s, areaId: v })} /></Field>}
      {s.scopeType === "case" && <Field label="Caso" required span={2}><EntityPicker kinds={["caso"]} value={s.ref} onChange={(v) => set({ ...s, ref: v })} /></Field>}
      {s.scopeType === "process" && <Field label="Processo" required span={2}><EntityPicker kinds={["processo"]} value={s.ref} onChange={(v) => set({ ...s, ref: v })} /></Field>}
    </>
  );
}
interface ScopeState { scopeType: "global" | "unit" | "area" | "case" | "process"; unitId: string | null; areaId: string | null; ref: EntityRef | null }
function scopeBody(s: ScopeState) {
  return {
    scopeType: s.scopeType, unitId: s.unitId, areaId: s.areaId,
    caseId: s.scopeType === "case" ? s.ref?.id : null, processId: s.scopeType === "process" ? s.ref?.id : null,
  };
}

function NewUserModal({ onClose }: { onClose: () => void }): JSX.Element {
  const { data: cat } = useCatalog();
  const [f, setF] = useState({ email: "", displayName: "", unitId: cat?.units[0]?.id ?? null as string | null, roleKey: "advogado", supervisorUserId: null as string | null });
  const [scope, setScope] = useState<ScopeState>({ scopeType: "unit", unitId: cat?.units[0]?.id ?? null, areaId: null, ref: null });
  const m = useAct(() => api.post<{ invited: boolean }>("/config/usuarios", {
    email: f.email, displayName: f.displayName, unitId: f.unitId, roles: f.roleKey ? [{ roleKey: f.roleKey, ...scopeBody(scope) }] : [], supervisorUserId: f.supervisorUserId,
  }), [["config-usuarios"], ["catalogo"]]);
  if (m.isSuccess) {
    return (
      <Modal title="Usuário criado" onClose={onClose} footer={<button className="btn primary" onClick={onClose}>OK</button>}>
        <div className="success">{m.data?.invited ? `Enviamos um e-mail para ${f.email} definir a senha.` : "A pessoa já tinha conta no VG APP e foi vinculada ao escritório."}</div>
      </Modal>
    );
  }
  return (
    <Modal title="Novo usuário" onClose={onClose} wide footer={<><button className="btn" onClick={onClose}>Cancelar</button><button className="btn primary" disabled={m.isPending || !f.email || f.displayName.length < 2 || !f.unitId} onClick={() => m.mutate()}>Criar e convidar</button></>}>
      <FormGrid cols={3}>
        <Field label="Nome" required><input className="input" value={f.displayName} onChange={(e) => setF({ ...f, displayName: e.target.value })} /></Field>
        <Field label="E-mail" required><input type="email" className="input" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} /></Field>
        <Field label="Lotação" required><UnitSelect value={f.unitId} onChange={(v) => setF({ ...f, unitId: v })} /></Field>
        <Field label="Papel inicial">
          <select className="input" value={f.roleKey} onChange={(e) => setF({ ...f, roleKey: e.target.value })}>
            <option value="">Nenhum</option>{cat?.roles.map((r) => <option key={r.key} value={r.key}>{r.name}</option>)}
          </select>
        </Field>
        <Field label="Advogado supervisor" hint="Para estagiários: vira o revisor padrão e acompanha a carga"><UserSelect value={f.supervisorUserId} onChange={(v) => setF({ ...f, supervisorUserId: v })} emptyLabel="Nenhum" /></Field>
        {f.roleKey && <ScopeFields s={scope} set={setScope} />}
      </FormGrid>
      <div className="muted small pad">A pessoa recebe um e-mail para criar a própria senha. O administrador nunca conhece a senha.</div>
      <ErrorBox error={m.error} />
    </Modal>
  );
}

function EditUserModal({ user, onClose }: { user: UserRow; onClose: () => void }): JSX.Element {
  const [f, setF] = useState({ displayName: user.displayName, unitId: user.unitId as string | null, active: user.active, supervisorUserId: (user.supervisorUserId ?? null) as string | null });
  const m = useAct(() => api.patch(`/config/usuarios/${user.id}`, { displayName: f.displayName, unitId: f.unitId ?? undefined, active: f.active, supervisorUserId: f.supervisorUserId }), [["config-usuarios"], ["catalogo"]], onClose);
  return (
    <Modal title={`Editar ${user.displayName}`} onClose={onClose} footer={<><button className="btn" onClick={onClose}>Cancelar</button><button className="btn primary" disabled={m.isPending} onClick={() => m.mutate()}>Salvar</button></>}>
      <div style={{ marginBottom: 14 }}><AvatarEditor userId={user.id} name={user.displayName} admin /></div>
      <FormGrid>
        <Field label="Nome"><input className="input" value={f.displayName} onChange={(e) => setF({ ...f, displayName: e.target.value })} /></Field>
        <Field label="E-mail" hint="O e-mail de acesso não muda aqui"><input className="input" value={user.email} disabled /></Field>
        <Field label="Lotação"><UnitSelect value={f.unitId} onChange={(v) => setF({ ...f, unitId: v })} /></Field>
        <Field label="Advogado supervisor" hint="Para estagiários: vira o revisor padrão e acompanha a carga"><UserSelect value={f.supervisorUserId} onChange={(v) => setF({ ...f, supervisorUserId: v })} emptyLabel="Nenhum" /></Field>
        <label className="check"><input type="checkbox" checked={f.active} onChange={(e) => setF({ ...f, active: e.target.checked })} /> Ativo (desmarcar encerra as sessões)</label>
      </FormGrid>
      <div className="muted small pad">Papéis e permissões: na lista de usuários (“atribuir papel”, “conceder permissão”).</div>
      <ErrorBox error={m.error} />
    </Modal>
  );
}

function GrantModal({ user, mode, onClose }: { user: UserRow; mode: "role" | "perm"; onClose: () => void }): JSX.Element {
  const { data: cat } = useCatalog();
  const [key, setKey] = useState(mode === "role" ? "advogado" : cat?.namedPermissions[0]?.key ?? "");
  const [reason, setReason] = useState("");
  const [scope, setScope] = useState<ScopeState>({ scopeType: "global", unitId: null, areaId: null, ref: null });
  const m = useAct(() => mode === "role"
    ? api.post(`/config/usuarios/${user.id}/papeis`, { roleKey: key, ...scopeBody(scope), reason: reason || null })
    : api.post(`/config/usuarios/${user.id}/permissoes`, { permission: key, ...scopeBody(scope), reason: reason || null }),
  [["config-usuarios"]], onClose);
  return (
    <Modal title={`${mode === "role" ? "Atribuir papel" : "Conceder permissão"} — ${user.displayName}`} onClose={onClose} wide footer={<><button className="btn" onClick={onClose}>Cancelar</button><button className="btn primary" disabled={m.isPending || !key} onClick={() => m.mutate()}>Confirmar</button></>}>
      <FormGrid cols={3}>
        <Field label={mode === "role" ? "Papel" : "Permissão"}>
          <select className="input" value={key} onChange={(e) => setKey(e.target.value)}>
            {mode === "role" ? cat?.roles.map((r) => <option key={r.key} value={r.key}>{r.name}</option>) : cat?.namedPermissions.map((p) => <option key={p.key} value={p.key}>{p.label}</option>)}
          </select>
        </Field>
        <ScopeFields s={scope} set={setScope} />
        <Field label="Motivo" span={3}><input className="input" value={reason} onChange={(e) => setReason(e.target.value)} /></Field>
      </FormGrid>
      {key === "controlador" && <div className="muted small pad">O Controlador é uma pessoa só no escritório; a mesma pessoa pode acumular outros papéis.</div>}
      <div className="muted small">A permissão vale imediatamente (as sessões da pessoa são atualizadas).</div>
      <ErrorBox error={m.error} />
    </Modal>
  );
}

// ================================================================== papéis

function Roles(): JSX.Element {
  const { data: cat } = useCatalog();
  const [edit, setEdit] = useState<Catalog["roles"][number] | "new" | null>(null);
  if (!cat) return <Loading />;
  return (
    <Card title="Papéis" actions={<button className="btn small primary" onClick={() => setEdit("new")}>Novo papel</button>}>
      <DataTable rows={cat.roles} rowKey={(r) => r.id} onRowClick={(r) => r.key !== "admin" && setEdit(r)}
        columns={[
          { key: "n", label: "Papel", render: (r) => <>{r.name} {r.system && <Badge tone="muted">padrão</Badge>}</> },
          { key: "a", label: "Ações", render: (r) => <span className="small">{r.actions.map((a) => ACTION_LABEL[a] ?? a).join(" · ")}</span> },
        ]} />
      <div className="muted small pad">Papel define o que a pessoa faz; o escopo (unidade, área, caso) é definido ao atribuir.</div>
      {edit && <RoleModal role={edit === "new" ? null : edit} all={cat.allActions} onClose={() => setEdit(null)} />}
    </Card>
  );
}

function RoleModal({ role, all, onClose }: { role: Catalog["roles"][number] | null; all: string[]; onClose: () => void }): JSX.Element {
  const [name, setName] = useState(role?.name ?? "");
  const [key, setKey] = useState(role?.key ?? "");
  const [acts, setActs] = useState<Set<string>>(new Set(role?.actions ?? []));
  const m = useAct(() => role
    ? api.put(`/config/papeis/${role.id}/acoes`, { name, actions: [...acts] })
    : api.post("/config/papeis", { key, name, actions: [...acts] }), CAT, onClose);
  return (
    <Modal title={role ? `Papel: ${role.name}` : "Novo papel"} onClose={onClose} wide footer={<><button className="btn" onClick={onClose}>Cancelar</button><button className="btn primary" disabled={m.isPending || name.length < 2 || (!role && !/^[a-z0-9_]{2,}$/.test(key))} onClick={() => m.mutate()}>Salvar</button></>}>
      <FormGrid cols={2}>
        <Field label="Nome"><input className="input" value={name} onChange={(e) => setName(e.target.value)} /></Field>
        {!role && <Field label="Chave" hint="minúsculas, sem espaço"><input className="input mono" value={key} onChange={(e) => setKey(e.target.value)} /></Field>}
      </FormGrid>
      <div className="grid cols-3" style={{ marginTop: 12, gap: 6 }}>
        {all.map((a) => (
          <label key={a} className="check small"><input type="checkbox" checked={acts.has(a)} onChange={(e) => { const n = new Set(acts); if (e.target.checked) n.add(a); else n.delete(a); setActs(n); }} /> {ACTION_LABEL[a] ?? a}</label>
        ))}
      </div>
      <ErrorBox error={m.error} />
    </Modal>
  );
}

// ============================================================ unidades/áreas

type UnitT = Catalog["units"][number];
type AreaT = Catalog["areas"][number];

function Units(): JSX.Element {
  const { data: cat } = useCatalog();
  const [unit, setUnit] = useState<string | null>(null);
  const [unitDlg, setUnitDlg] = useState<UnitT | "new" | null>(null);
  const setAreas = useAct((v: { unitId: string; areaIds: string[] }) => api.put(`/config/unidades/${v.unitId}/areas`, { areaIds: v.areaIds }), CAT);
  if (!cat) return <Loading />;
  const u = cat.units.find((x) => x.id === (unit ?? cat.units[0]?.id));
  return (
    <div className="stack gap-16">
      <Card title="Unidades" actions={<button className="btn small primary" onClick={() => setUnitDlg("new")}>Nova unidade</button>}>
        <DataTable rows={cat.units} rowKey={(x) => x.id} onRowClick={(x) => setUnit(x.id)} rowClass={(x) => (x.id === u?.id ? "row-warn" : x.active ? "" : "row-muted")}
          columns={[
            { key: "n", label: "Unidade", render: (x) => <>{x.name}{!x.active && <Badge tone="muted">inativa</Badge>}<div className="muted small">{x.legalEntityName ?? ""}{x.legalDocument ? ` · ${x.legalDocument}` : ""}</div></> },
            { key: "k", label: "Tipo", width: 110, render: (x) => x.kind },
            { key: "a", label: "Áreas atendidas", width: 130, render: (x) => x.areaIds.length },
            { key: "e", label: "", width: 80, render: (x) => <button className="btn small" onClick={(ev) => { ev.stopPropagation(); setUnitDlg(x); }}>Editar</button> },
          ]} />
        {u && (
          <>
            <hr className="sep" />
            <div className="fld-label">Áreas atendidas por {u.name} <span className="muted">(clique numa unidade acima para trocar)</span></div>
            <div className="grid cols-3" style={{ gap: 4, marginTop: 6 }}>
              {cat.areas.map((a) => (
                <label key={a.id} className="check small"><input type="checkbox" checked={u.areaIds.includes(a.id)}
                  onChange={(e) => setAreas.mutate({ unitId: u.id, areaIds: e.target.checked ? [...u.areaIds, a.id] : u.areaIds.filter((x) => x !== a.id) })} /> {a.name}</label>
              ))}
            </div>
            <ErrorBox error={setAreas.error} />
          </>
        )}
      </Card>
      {unitDlg && <UnitModal unit={unitDlg === "new" ? null : unitDlg} onClose={() => setUnitDlg(null)} />}
    </div>
  );
}

function AreasTab(): JSX.Element {
  const { data: cat } = useCatalog();
  if (!cat) return <Loading />;
  return <AreasAndActions areas={cat.areas} />;
}

function AreasAndActions({ areas }: { areas: AreaT[] }): JSX.Element {
  const [areaId, setAreaId] = useState<string | null>(areas[0]?.id ?? null);
  const [dlg, setDlg] = useState<null | { kind: "area"; area: AreaT | null } | { kind: "acao"; areaId: string; item: { id: string; name: string; active: boolean } | null }>(null);
  const area = areas.find((a) => a.id === areaId) ?? areas[0];
  const toggleArea = useAct((v: { id: string; active: boolean }) => api.patch(`/config/areas/${v.id}`, { active: v.active }), CAT);
  const toggleAction = useAct((v: { id: string; active: boolean }) => api.patch(`/config/assuntos/${v.id}`, { active: v.active }), CAT);
  return (
    <Card title="Áreas e tipos de ação" actions={<button className="btn small primary" onClick={() => setDlg({ kind: "area", area: null })}>Nova área</button>}>
      <div className="muted small pad">Hierarquia: <strong>Área</strong> (ex.: Família) → <strong>Tipo de ação</strong> (ex.: Alimentos, Divórcio) → no dia a dia, <strong>tipo de atividade</strong> (ex.: Petição inicial).</div>
      <div className="grid" style={{ gridTemplateColumns: "minmax(220px, 1fr) 2fr" }}>
        <div className="stack" style={{ gap: 2 }}>
          {areas.map((a) => (
            <div key={a.id} className={`row between ${a.id === area?.id ? "row-sel" : ""}`} style={{ padding: "6px 8px", borderRadius: 6, cursor: "pointer", background: a.id === area?.id ? "var(--surface)" : undefined }} onClick={() => setAreaId(a.id)}>
              <span className={a.active ? "" : "muted"}>{a.name} <span className="muted small">({a.subjects.filter((s) => s.active).length})</span></span>
              {!a.active && <Badge tone="muted">inativa</Badge>}
            </div>
          ))}
        </div>
        {area && (
          <div>
            <div className="row between" style={{ marginBottom: 8 }}>
              <strong>{area.name}</strong>
              <div className="row">
                <button className="btn small" onClick={() => setDlg({ kind: "area", area })}>Renomear</button>
                <button className="btn small" onClick={() => toggleArea.mutate({ id: area.id, active: !area.active })}>{area.active ? "Desativar área" : "Reativar área"}</button>
                <button className="btn small primary" onClick={() => setDlg({ kind: "acao", areaId: area.id, item: null })}>Novo tipo de ação</button>
              </div>
            </div>
            <DataTable rows={area.subjects} rowKey={(s) => s.id} rowClass={(s) => (s.active ? "" : "row-muted")} empty="Nenhum tipo de ação nesta área."
              columns={[
                { key: "n", label: "Tipo de ação", render: (s) => s.name },
                { key: "a", label: "Ativo", width: 70, render: (s) => <input type="checkbox" checked={s.active} onChange={(e) => toggleAction.mutate({ id: s.id, active: e.target.checked })} /> },
                { key: "e", label: "", width: 80, render: (s) => <button className="btn small" onClick={() => setDlg({ kind: "acao", areaId: area.id, item: s })}>Editar</button> },
              ]} />
          </div>
        )}
      </div>
      <ErrorBox error={toggleArea.error ?? toggleAction.error} />
      {dlg?.kind === "area" && <NameModal title={dlg.area ? "Renomear área" : "Nova área"} initial={dlg.area?.name ?? ""} onClose={() => setDlg(null)}
        save={(name) => (dlg.area ? api.patch(`/config/areas/${dlg.area.id}`, { name }) : api.post("/config/areas", { name }))} />}
      {dlg?.kind === "acao" && <NameModal title={dlg.item ? "Editar tipo de ação" : "Novo tipo de ação"} initial={dlg.item?.name ?? ""} onClose={() => setDlg(null)}
        save={(name) => (dlg.item ? api.patch(`/config/assuntos/${dlg.item.id}`, { name }) : api.post(`/config/areas/${dlg.areaId}/assuntos`, { name }))} />}
    </Card>
  );
}

/** Modal simples de um campo "nome". */
function NameModal({ title, initial, save, onClose, invalidate = CAT }: { title: string; initial: string; save: (name: string) => Promise<unknown>; onClose: () => void; invalidate?: string[][] }): JSX.Element {
  const [name, setName] = useState(initial);
  const m = useAct(() => save(name.trim()), invalidate, onClose);
  return (
    <Modal title={title} onClose={onClose} footer={<><button className="btn" onClick={onClose}>Cancelar</button><button className="btn primary" disabled={m.isPending || name.trim().length < 2} onClick={() => m.mutate()}>Salvar</button></>}>
      <Field label="Nome"><input className="input" value={name} onChange={(e) => setName(e.target.value)} autoFocus onKeyDown={(e) => { if (e.key === "Enter" && name.trim().length >= 2) m.mutate(); }} /></Field>
      <ErrorBox error={m.error} />
    </Modal>
  );
}

function UnitModal({ unit, onClose }: { unit: UnitT | null; onClose: () => void }): JSX.Element {
  const [f, setF] = useState({ name: unit?.name ?? "", kind: unit?.kind ?? "filial", legalEntityName: unit?.legalEntityName ?? "", legalDocument: unit?.legalDocument ?? "", active: unit?.active ?? true });
  const body = { ...f, legalEntityName: f.legalEntityName || null, legalDocument: f.legalDocument || null };
  const m = useAct(() => (unit ? api.patch(`/config/unidades/${unit.id}`, body) : api.post("/config/unidades", body)), CAT, onClose);
  return (
    <Modal title={unit ? `Editar ${unit.name}` : "Nova unidade"} onClose={onClose} footer={<><button className="btn" onClick={onClose}>Cancelar</button><button className="btn primary" disabled={m.isPending || f.name.length < 2} onClick={() => m.mutate()}>Salvar</button></>}>
      <FormGrid>
        <Field label="Nome" required><input className="input" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} /></Field>
        <Field label="Tipo"><select className="input" value={f.kind} onChange={(e) => setF({ ...f, kind: e.target.value })}><option value="escritorio">Escritório</option><option value="filial">Filial</option><option value="sociedade">Sociedade</option></select></Field>
        <Field label="Razão social"><input className="input" value={f.legalEntityName} onChange={(e) => setF({ ...f, legalEntityName: e.target.value })} /></Field>
        <Field label="CNPJ"><input className="input" value={f.legalDocument} onChange={(e) => setF({ ...f, legalDocument: e.target.value })} /></Field>
        {unit && <label className="check"><input type="checkbox" checked={f.active} onChange={(e) => setF({ ...f, active: e.target.checked })} /> Ativa</label>}
      </FormGrid>
      <ErrorBox error={m.error} />
    </Modal>
  );
}

// ========================================================= tipos de atividade

type ActTypeT = Catalog["activityTypes"][number];

export function ActivityTypes(): JSX.Element {
  const { data: cat } = useCatalog();
  const [dlg, setDlg] = useState<ActTypeT | "new" | null>(null);
  const patch = useAct((v: { id: string; body: Record<string, unknown> }) => api.patch(`/config/tipos-atividade/${v.id}`, v.body), CAT);
  if (!cat) return <Loading />;
  return (
    <Card title="Tipos de atividade" actions={<button className="btn small primary" onClick={() => setDlg("new")}>Novo tipo</button>}>
      <div className="muted small pad">O que é feito no dia a dia: petição inicial, contestação, audiência, protocolo… Vale para qualquer área e tipo de ação.</div>
      <DataTable rows={cat.activityTypes} rowKey={(t) => t.id} rowClass={(t) => (t.active ? "" : "row-muted")}
        columns={[
          { key: "n", label: "Tipo", render: (t) => t.name },
          { key: "r", label: "Pede revisão processual", width: 170, render: (t) => (t.requiresProcessReview ? <Badge tone="info">sim</Badge> : "—") },
          { key: "e", label: "Exige comprovante", width: 140, render: (t) => (t.requiresEvidence ? <Badge tone="info">sim</Badge> : "—") },
          { key: "k", label: "Risco padrão", width: 110, render: (t) => t.defaultRisk },
          { key: "a", label: "Ativo", width: 60, render: (t) => <input type="checkbox" checked={t.active} onChange={(e) => patch.mutate({ id: t.id, body: { active: e.target.checked } })} /> },
          { key: "x", label: "", width: 80, render: (t) => <button className="btn small" onClick={() => setDlg(t)}>Editar</button> },
        ]} />
      <ErrorBox error={patch.error} />
      {dlg && <ActivityTypeModal t={dlg === "new" ? null : dlg} onClose={() => setDlg(null)} />}
    </Card>
  );
}

function ActivityTypeModal({ t, onClose }: { t: ActTypeT | null; onClose: () => void }): JSX.Element {
  const [f, setF] = useState({ name: t?.name ?? "", requiresProcessReview: t?.requiresProcessReview ?? false, requiresEvidence: t?.requiresEvidence ?? false, defaultRisk: t?.defaultRisk ?? "medio", points: t?.points ?? 1, checklistRequired: t?.checklistRequired ?? false });
  const [checkText, setCheckText] = useState((Array.isArray(t?.defaultChecklist) ? t!.defaultChecklist : []).join("\n"));
  const key = f.name.normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_|_$/g, "").slice(0, 60);
  const body = (): Record<string, unknown> => ({ ...f, points: Math.max(0, Math.round(Number(f.points) || 0)), defaultChecklist: checkText.split("\n").map((x) => x.trim()).filter(Boolean).slice(0, 60) });
  const m = useAct(() => (t ? api.patch(`/config/tipos-atividade/${t.id}`, body()) : api.post("/config/tipos-atividade", { ...body(), key })), CAT, onClose);
  return (
    <Modal title={t ? "Editar tipo de atividade" : "Novo tipo de atividade"} onClose={onClose} footer={<><button className="btn" onClick={onClose}>Cancelar</button><button className="btn primary" disabled={m.isPending || f.name.trim().length < 2} onClick={() => m.mutate()}>Salvar</button></>}>
      <FormGrid cols={1}>
        <Field label="Nome" required><input className="input" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} autoFocus /></Field>
        <Field label="Risco padrão"><select className="input" value={f.defaultRisk} onChange={(e) => setF({ ...f, defaultRisk: e.target.value })}><option value="baixo">baixo</option><option value="medio">médio</option><option value="alto">alto</option></select></Field>
        <label className="check"><input type="checkbox" checked={f.requiresProcessReview} onChange={(e) => setF({ ...f, requiresProcessReview: e.target.checked })} /> Ao concluir, pedir revisão da situação processual</label>
        <label className="check"><input type="checkbox" checked={f.requiresEvidence} onChange={(e) => setF({ ...f, requiresEvidence: e.target.checked })} /> Exigir comprovante (ex.: nº do protocolo)</label>
        <Field label="Pontos" hint="Valor base. É multiplicado pelo peso do tipo de ação; fora do prazo interno vale o percentual definido em Pontuação."><input className="input" type="number" min={0} max={1000} value={f.points} onChange={(e) => setF({ ...f, points: Number(e.target.value) })} style={{ maxWidth: 120 }} /></Field>
        <Field label="Checklist padrão" hint="Um item por linha. Vem preenchido ao criar a atividade (pode ser alterado nela)."><textarea className="input" rows={5} value={checkText} onChange={(e) => setCheckText(e.target.value)} placeholder={"Conferir procuração\nAnexar documentos\nProtocolar"} /></Field>
        <label className="check"><input type="checkbox" checked={f.checklistRequired} onChange={(e) => setF({ ...f, checklistRequired: e.target.checked })} /> Só concluir com todos os itens do checklist marcados</label>
      </FormGrid>
      <ErrorBox error={m.error} />
    </Modal>
  );
}

// =================================================================== etapas

type StageT = Catalog["stages"][number];

export function Stages(): JSX.Element {
  const { data: cat } = useCatalog();
  const [dlg, setDlg] = useState<{ scope: "processo" | "caso"; stage: StageT | null } | null>(null);
  const [del, setDel] = useState<StageT | null>(null);
  const reorder = useAct((ids: string[]) => api.put("/config/etapas/ordem", { ids }), CAT);
  if (!cat) return <Loading />;
  const col = (scope: "processo" | "caso") => {
    const list = cat.stages.filter((s) => s.scope === scope);
    const move = (i: number, d: -1 | 1) => {
      const ids = list.map((s) => s.id);
      const j = i + d;
      if (j < 0 || j >= ids.length) return;
      [ids[i], ids[j]] = [ids[j]!, ids[i]!];
      reorder.mutate(ids);
    };
    return (
      <Card title={scope === "processo" ? "Etapas de processos" : "Etapas de casos"} actions={<button className="btn small primary" onClick={() => setDlg({ scope, stage: null })}>Nova etapa</button>}>
        <DataTable rows={list} rowKey={(s) => s.id} rowClass={(s) => (s.active ? "" : "row-muted")}
          columns={[
            { key: "o", label: "", width: 64, render: (s) => { const i = list.indexOf(s); return <div className="row nowrap"><button className="btn small" disabled={i === 0} onClick={() => move(i, -1)}>↑</button><button className="btn small" disabled={i === list.length - 1} onClick={() => move(i, 1)}>↓</button></div>; } },
            { key: "n", label: "Etapa", render: (s) => <span><span className="dot" style={{ background: s.color, display: "inline-block", width: 10, height: 10, borderRadius: 5, marginRight: 6 }} />{s.name} {s.isFinal && <Badge tone="muted">final</Badge>}</span> },
            { key: "e", label: "", width: 160, render: (s) => <div className="row"><button className="btn small" onClick={() => setDlg({ scope, stage: s })}>Editar</button><button className="btn small danger" onClick={() => setDel(s)}>Remover</button></div> },
          ]} />
      </Card>
    );
  };
  return (
    <div className="stack gap-16">
      <div className="muted small">As etapas formam as colunas dos kanbans. Etapa <strong>final</strong> (arquivado/encerrado) fica oculta no kanban por padrão. A ordem aqui é a ordem das colunas.</div>
      <div className="grid cols-2">{col("processo")}{col("caso")}</div>
      <ErrorBox error={reorder.error} />
      {dlg && <StageModal scope={dlg.scope} stage={dlg.stage} onClose={() => setDlg(null)} />}
      {del && <DeleteStageModal stage={del} others={cat.stages.filter((x) => x.scope === del.scope && x.id !== del.id)} onClose={() => setDel(null)} />}
    </div>
  );
}

function DeleteStageModal({ stage, others, onClose }: { stage: StageT; others: StageT[]; onClose: () => void }): JSX.Element {
  const [dest, setDest] = useState<string>(others[0]?.id ?? "none");
  const m = useAct(() => api.del<{ moved: number }>(`/config/etapas/${stage.id}?destino=${dest}`), [["catalogo"], ["quadro-dados"], ["processos"], ["casos"]], onClose);
  return (
    <Modal title={`Remover etapa "${stage.name}"`} onClose={onClose} footer={<><button className="btn" onClick={onClose}>Cancelar</button><button className="btn danger" disabled={m.isPending} onClick={() => m.mutate()}>Remover</button></>}>
      <div className="stack">
        <div>Os {stage.scope === "processo" ? "processos" : "casos"} que estão nesta etapa vão para:</div>
        <select className="input" value={dest} onChange={(e) => setDest(e.target.value)}>
          {others.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
          <option value="none">Sem etapa</option>
        </select>
        <div className="muted small">A mudança fica registrada na auditoria. Se preferir não mexer nos itens, apenas desative a etapa em “Editar”.</div>
        <ErrorBox error={m.error} />
      </div>
    </Modal>
  );
}

function StageModal({ scope, stage, onClose }: { scope: "processo" | "caso"; stage: StageT | null; onClose: () => void }): JSX.Element {
  const [f, setF] = useState({ name: stage?.name ?? "", color: stage?.color ?? "#34506F", isFinal: stage?.isFinal ?? false, active: stage?.active ?? true });
  const m = useAct(() => (stage ? api.patch(`/config/etapas/${stage.id}`, f) : api.post("/config/etapas", { ...f, scope })), [["catalogo"], ["kanban"]], onClose);
  return (
    <Modal title={stage ? "Editar etapa" : `Nova etapa de ${scope}`} onClose={onClose} footer={<><button className="btn" onClick={onClose}>Cancelar</button><button className="btn primary" disabled={m.isPending || f.name.trim().length < 2} onClick={() => m.mutate()}>Salvar</button></>}>
      <FormGrid>
        <Field label="Nome" required span={2}><input className="input" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} autoFocus /></Field>
        <Field label="Cor"><input type="color" className="input" style={{ height: 36, padding: 2 }} value={f.color} onChange={(e) => setF({ ...f, color: e.target.value })} /></Field>
        <div className="stack">
          <label className="check"><input type="checkbox" checked={f.isFinal} onChange={(e) => setF({ ...f, isFinal: e.target.checked })} /> Etapa final</label>
          {stage && <label className="check"><input type="checkbox" checked={f.active} onChange={(e) => setF({ ...f, active: e.target.checked })} /> Ativa</label>}
        </div>
      </FormGrid>
      <ErrorBox error={m.error} />
    </Modal>
  );
}

// ==================================================================== OABs

interface OabRow { id: string; number: string; uf: string; holderName: string; nameVariants: string[]; userId: string | null; active: boolean; lastCheckpoint: string | null }

function unaccent(s: string): string {
  return s.normalize("NFD").replace(/[\u0300-\u036f]/g, "");
}

/** Nomes que a captura vai consultar (espelha a regra do worker). */
function searchedNames(holder: string, variants: string[]): string[] {
  const out = new Set<string>();
  for (const n of [holder, ...variants].map((x) => x.replace(/\s+/g, " ").trim()).filter((x) => x.split(" ").length >= 2)) {
    out.add(n.toUpperCase());
    out.add(unaccent(n).toUpperCase());
  }
  return [...out];
}

function Oabs(): JSX.Element {
  const { userName } = useLookups();
  const inv = [["config-oabs"], ["controladoria"]];
  const { data, error } = useQuery({ queryKey: ["config-oabs"], queryFn: () => api.get<OabRow[]>("/config/oabs") });
  const [f, setF] = useState({ number: "", uf: "SP", holderName: "", variants: "", userId: null as string | null });
  const [edit, setEdit] = useState<OabRow | null>(null);
  const variantsOf = (v: string) => v.split(/\n|;/).map((x) => x.trim()).filter((x) => x.length >= 3);
  const create = useAct(() => api.post("/config/oabs", { number: f.number, uf: f.uf, holderName: f.holderName, nameVariants: variantsOf(f.variants), userId: f.userId }),
    inv, () => setF({ number: "", uf: f.uf, holderName: "", variants: "", userId: null }));
  const patch = useAct((v: { id: string; body: Record<string, unknown> }) => api.patch(`/config/oabs/${v.id}`, v.body), inv);
  return (
    <div className="stack gap-16">
      <Card title="OABs monitoradas no DJEN">
        <div className="muted small pad">
          A consulta é <strong>nacional</strong>: a UF é a da inscrição, não a do tribunal — uma OAB/PE recebe publicações de tribunais de
          qualquer estado. Inscrição suplementar em outro estado (ex.: OAB/BA) é cadastrada como outra linha, ligada ao mesmo usuário.
          A captura consulta cada OAB pelo número <strong>e</strong> pelo nome do advogado (titular e nomes alternativos, como
          solteira e casada). Cada nome é consultado com e sem acento, automaticamente. Publicações de homônimos com outra OAB são descartadas.
        </div>
        <ErrorBox error={error ?? create.error ?? patch.error} />
        <DataTable rows={data} rowKey={(o) => o.id} empty="Nenhuma OAB. Sem OAB, não há captura automática."
          columns={[
            { key: "n", label: "OAB", width: 130, render: (o) => <>{o.number}/{o.uf}<div className="muted small">{(data ?? []).filter((x) => x.userId && x.userId === o.userId).length > 1 ? "inscrição de pessoa com mais de uma OAB" : "abrangência nacional"}</div></> },
            { key: "h", label: "Nomes consultados", render: (o) => (
              <>
                <div>{o.holderName}{o.userId && <span className="muted small"> · {userName(o.userId)}</span>}</div>
                {o.nameVariants.map((v) => <div key={v} className="small">{v}</div>)}
                <div className="muted small">{searchedNames(o.holderName, o.nameVariants).length} consulta(s) por nome</div>
              </>
            ) },
            { key: "c", label: "Capturado até", width: 120, render: (o) => fmtDate(o.lastCheckpoint) },
            { key: "a", label: "Ativa", width: 60, render: (o) => <input type="checkbox" checked={o.active} onChange={(e) => patch.mutate({ id: o.id, body: { active: e.target.checked } })} /> },
            { key: "r", label: "", width: 190, render: (o) => (
              <div className="row">
                <button className="btn small" onClick={() => setEdit(o)}>Editar</button>
                <button className="btn small" title="Recaptura os últimos 15 dias no próximo ciclo" onClick={() => patch.mutate({ id: o.id, body: { lastCheckpoint: null } })}>Recapturar</button>
              </div>
            ) },
          ]} />
      </Card>
      <Card title="Adicionar OAB">
        <FormGrid cols={3}>
          <Field label="Número" required><input className="input" value={f.number} onChange={(e) => setF({ ...f, number: e.target.value })} /></Field>
          <Field label="UF" required><input className="input" maxLength={2} value={f.uf} onChange={(e) => setF({ ...f, uf: e.target.value.toUpperCase() })} /></Field>
          <Field label="Usuário (opcional)"><UserSelect value={f.userId} onChange={(v) => setF({ ...f, userId: v })} emptyLabel="—" /></Field>
          <Field label="Nome completo do titular" required span={3} hint="Escreva com os acentos corretos; a versão sem acento é consultada também.">
            <input className="input" value={f.holderName} onChange={(e) => setF({ ...f, holderName: e.target.value })} />
          </Field>
          <Field label="Outros nomes usados em publicações" span={3} hint="Um por linha. Ex.: nome de solteira e nome de casada, ou abreviações que os tribunais usam.">
            <textarea className="input" rows={3} value={f.variants} onChange={(e) => setF({ ...f, variants: e.target.value })} />
          </Field>
        </FormGrid>
        {f.holderName && <div className="muted small pad">Serão consultados: {searchedNames(f.holderName, variantsOf(f.variants)).join(" · ")}</div>}
        <button className="btn primary" disabled={create.isPending || !f.number || f.holderName.trim().split(/\s+/).length < 2} onClick={() => create.mutate()}>Adicionar</button>
      </Card>
      {edit && <OabNamesModal oab={edit} onClose={() => setEdit(null)} />}
    </div>
  );
}

function OabNamesModal({ oab, onClose }: { oab: OabRow; onClose: () => void }): JSX.Element {
  const [f, setF] = useState({ number: oab.number, uf: oab.uf, holderName: oab.holderName, variants: oab.nameVariants.join("\n"), userId: oab.userId });
  const list = f.variants.split(/\n|;/).map((x) => x.trim()).filter((x) => x.length >= 3);
  const m = useAct(() => api.patch(`/config/oabs/${oab.id}`, { number: f.number, uf: f.uf, holderName: f.holderName, nameVariants: list, userId: f.userId }),
    [["config-oabs"], ["controladoria"]], onClose);
  return (
    <Modal title={`Editar OAB ${oab.number}/${oab.uf}`} onClose={onClose} footer={<><button className="btn" onClick={onClose}>Cancelar</button><button className="btn primary" disabled={m.isPending || f.holderName.trim().split(/\s+/).length < 2 || !f.number} onClick={() => m.mutate()}>Salvar</button></>}>
      <FormGrid cols={3}>
        <Field label="Número"><input className="input" value={f.number} onChange={(e) => setF({ ...f, number: e.target.value })} /></Field>
        <Field label="UF da inscrição"><input className="input" maxLength={2} value={f.uf} onChange={(e) => setF({ ...f, uf: e.target.value.toUpperCase() })} /></Field>
        <Field label="Usuário"><UserSelect value={f.userId} onChange={(v) => setF({ ...f, userId: v })} emptyLabel="—" /></Field>
        <Field label="Nome do titular" span={3}><input className="input" value={f.holderName} onChange={(e) => setF({ ...f, holderName: e.target.value })} /></Field>
        <Field label="Outros nomes (um por linha)" span={3}><textarea className="input" rows={4} value={f.variants} onChange={(e) => setF({ ...f, variants: e.target.value })} /></Field>
      </FormGrid>
      <div className="muted small pad">Consultas por nome: {searchedNames(f.holderName, list).join(" · ")}</div>
      <div className="muted small">Ao mudar nomes, a captura refaz os últimos 15 dias.</div>
      <ErrorBox error={m.error} />
    </Modal>
  );
}

// ================================================================ feriados

function Holidays(): JSX.Element {
  const [year, setYear] = useState(new Date().getFullYear());
  const inv = [["config-feriados"]];
  const { data, error } = useQuery({ queryKey: ["config-feriados", year], queryFn: () => api.get<Array<{ id: string; date: string; name: string; scope: string; organName: string }>>(`/config/feriados?ano=${year}`) });
  const [f, setF] = useState({ date: "", name: "", scope: "orgao", organName: "" });
  const gen = useAct(() => api.post<{ created: number }>("/config/feriados/gerar", { ano: year }), inv);
  const create = useAct(() => api.post("/config/feriados", f), inv, () => setF({ ...f, date: "", name: "" }));
  const del = useAct((id: string) => api.del(`/config/feriados/${id}`), inv);
  const [editH, setEditH] = useState<{ id: string; date: string; name: string; scope: string; organName: string } | null>(null);
  return (
    <Card title="Feriados" actions={<>
      <select className="input" value={year} onChange={(e) => setYear(Number(e.target.value))}>{[year - 1, year, year + 1, year + 2].map((y) => <option key={y}>{y}</option>)}</select>
      <button className="btn small" disabled={gen.isPending} onClick={() => gen.mutate()}>Gerar nacionais de {year}</button>
    </>}>
      {gen.isSuccess && <div className="success">{gen.data?.created} feriado(s) criados.</div>}
      <ErrorBox error={error ?? gen.error ?? create.error ?? del.error} />
      <div className="muted small pad">Processuais (nacional/órgão) contam para prazos sugeridos; financeiros movem vencimentos de repasse para o próximo dia útil.</div>
      <DataTable rows={data} rowKey={(h) => h.id} empty="Nenhum feriado no ano."
        columns={[
          { key: "d", label: "Data", width: 110, render: (h) => fmtDate(h.date) },
          { key: "n", label: "Feriado", render: (h) => h.name },
          { key: "s", label: "Tipo", render: (h) => <Badge tone={h.scope === "financeiro" ? "info" : h.scope === "orgao" ? "warn" : "neutral"}>{h.scope}</Badge> },
          { key: "o", label: "Órgão", render: (h) => h.organName || "—" },
          { key: "x", label: "", width: 160, render: (h) => <div className="row"><button className="btn small" onClick={() => setEditH(h)}>Editar</button><button className="btn small" onClick={() => del.mutate(h.id)}>Remover</button></div> },
        ]} />
      <hr className="sep" />
      <div className="row nowrap">
        <input type="date" className="input" style={{ width: 160 }} value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} />
        <input className="input" placeholder="Nome" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} />
        <select className="input" style={{ width: 140 }} value={f.scope} onChange={(e) => setF({ ...f, scope: e.target.value })}><option value="orgao">Órgão/local</option><option value="nacional">Nacional</option><option value="financeiro">Financeiro</option></select>
        {f.scope === "orgao" && <input className="input" placeholder="Órgão (igual ao cadastro do processo)" value={f.organName} onChange={(e) => setF({ ...f, organName: e.target.value })} />}
        <button className="btn primary" disabled={create.isPending || !f.date || f.name.length < 2} onClick={() => create.mutate()}>Adicionar</button>
      </div>
      {editH && <HolidayModal h={editH} onClose={() => setEditH(null)} />}
    </Card>
  );
}

function HolidayModal({ h, onClose }: { h: { id: string; date: string; name: string; scope: string; organName: string }; onClose: () => void }): JSX.Element {
  const [f, setF] = useState({ date: h.date.slice(0, 10), name: h.name, scope: h.scope, organName: h.organName });
  const m = useAct(() => api.patch(`/config/feriados/${h.id}`, f), [["config-feriados"]], onClose);
  return (
    <Modal title="Editar feriado" onClose={onClose} footer={<><button className="btn" onClick={onClose}>Cancelar</button><button className="btn primary" disabled={m.isPending || f.name.length < 2} onClick={() => m.mutate()}>Salvar</button></>}>
      <FormGrid>
        <Field label="Data"><input type="date" className="input" value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} /></Field>
        <Field label="Tipo"><select className="input" value={f.scope} onChange={(e) => setF({ ...f, scope: e.target.value })}><option value="nacional">Nacional</option><option value="orgao">Órgão/local</option><option value="financeiro">Financeiro</option></select></Field>
        <Field label="Nome" span={2}><input className="input" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} /></Field>
        {f.scope === "orgao" && <Field label="Órgão" span={2}><input className="input" value={f.organName} onChange={(e) => setF({ ...f, organName: e.target.value })} /></Field>}
      </FormGrid>
      <ErrorBox error={m.error} />
    </Modal>
  );
}

// ============================================================== automações

interface RuleRow { id: string; name: string; eventType: string; active: boolean; version: number; conditions: unknown; actions: Array<Record<string, unknown>>; executions: Record<string, number> }

export function Rules(): JSX.Element {
  const { data: cat } = useCatalog();
  const { data, error } = useQuery({ queryKey: ["config-regras"], queryFn: () => api.get<RuleRow[]>("/config/regras") });
  const [edit, setEdit] = useState<RuleRow | "new" | null>(null);
  const [execs, setExecs] = useState<string | null>(null);
  const toggle = useAct((v: { id: string; active: boolean }) => api.patch(`/config/regras/${v.id}`, { active: v.active }), [["config-regras"]]);
  const label = (t: string) => cat?.ruleEvents.find((e) => e.type === t)?.label ?? t;
  return (
    <Card title="Automações" actions={<button className="btn small primary" onClick={() => setEdit("new")}>Nova automação</button>}>
      <div className="muted small pad">Quando um evento acontece e as condições batem, o sistema executa as ações. Cada evento roda uma única vez por regra; ações de automação não disparam outras automações.</div>
      <ErrorBox error={error ?? toggle.error} />
      <DataTable rows={data} rowKey={(r) => r.id} empty="Nenhuma automação."
        columns={[
          { key: "n", label: "Automação", render: (r) => <>{r.name}<div className="muted small">v{r.version}</div></> },
          { key: "e", label: "Quando", render: (r) => label(r.eventType) },
          { key: "a", label: "Faz", render: (r) => r.actions.map((a, i) => <div key={i} className="small">{a.type === "email" ? `E-mail para ${String(a.to)}` : `Cria atividade "${String(a.title)}"`}</div>) },
          { key: "x", label: "Execuções", render: (r) => <button className="link-btn small" onClick={() => setExecs(r.id)}>{Object.entries(r.executions).map(([k, v]) => `${k}: ${v}`).join(" · ") || "nenhuma"}</button> },
          { key: "on", label: "Ativa", render: (r) => <input type="checkbox" checked={r.active} onChange={(e) => toggle.mutate({ id: r.id, active: e.target.checked })} /> },
          { key: "ed", label: "", render: (r) => <button className="btn small" onClick={() => setEdit(r)}>Editar</button> },
        ]} />
      {edit && <RuleModal rule={edit === "new" ? null : edit} onClose={() => setEdit(null)} />}
      {execs && <ExecModal ruleId={execs} onClose={() => setExecs(null)} />}
    </Card>
  );
}

interface CondRow { field: string; op: string; value: string }
interface ActRow { type: "criar_atividade" | "email"; title: string; typeKey: string; responsible: string; days: string; priority: string; to: string; subject: string; body: string }

function RuleModal({ rule, onClose }: { rule: RuleRow | null; onClose: () => void }): JSX.Element {
  const { data: cat } = useCatalog();
  const [name, setName] = useState(rule?.name ?? "");
  const [eventType, setEventType] = useState(rule?.eventType ?? cat?.ruleEvents[0]?.type ?? "");
  const initConds = ((rule?.conditions as { all?: Array<{ field: string; op: string; value?: unknown }> } | null)?.all ?? []).map((c) => ({ field: c.field, op: c.op, value: Array.isArray(c.value) ? c.value.join(",") : String(c.value ?? "") }));
  const [conds, setConds] = useState<CondRow[]>(initConds);
  const [acts, setActs] = useState<ActRow[]>((rule?.actions ?? [{ type: "criar_atividade" }]).map((a) => ({
    type: (a.type as ActRow["type"]) ?? "criar_atividade", title: String(a.title ?? ""), typeKey: String(a.typeKey ?? ""), responsible: String(a.responsible ?? "processo"),
    days: a.internalDueInBusinessDays !== undefined ? String(a.internalDueInBusinessDays) : "5", priority: String(a.priority ?? "media"),
    to: String(a.to ?? "responsavel_processo"), subject: String(a.subject ?? ""), body: String(a.body ?? ""),
  })));
  const fields = cat?.ruleEvents.find((e) => e.type === eventType)?.fields ?? [];
  const body = () => ({
    name, eventType,
    conditions: { all: conds.filter((c) => c.field).map((c) => ({ field: c.field, op: c.op, value: c.op === "in" ? c.value.split(",").map((s) => s.trim()) : c.op === "exists" ? undefined : c.value })) },
    actions: acts.map((a) => a.type === "email"
      ? { type: "email", to: a.to, subject: a.subject, body: a.body }
      : { type: "criar_atividade", title: a.title, typeKey: a.typeKey || undefined, responsible: a.responsible, internalDueInBusinessDays: a.days ? Number(a.days) : undefined, priority: a.priority }),
  });
  const m = useAct(() => (rule ? api.put(`/config/regras/${rule.id}`, body()) : api.post("/config/regras", body())), [["config-regras"]], onClose);
  const upA = (i: number, p: Partial<ActRow>) => setActs(acts.map((x, j) => (j === i ? { ...x, ...p } : x)));

  return (
    <Modal title={rule ? `Editar automação (nova versão)` : "Nova automação"} onClose={onClose} wide footer={<><button className="btn" onClick={onClose}>Cancelar</button><button className="btn primary" disabled={m.isPending || name.length < 3 || !acts.length} onClick={() => m.mutate()}>Salvar</button></>}>
      <div className="stack">
        <FormGrid cols={2}>
          <Field label="Nome" required><input className="input" value={name} onChange={(e) => setName(e.target.value)} /></Field>
          <Field label="Quando acontecer"><select className="input" value={eventType} onChange={(e) => setEventType(e.target.value)}>{cat?.ruleEvents.map((e) => <option key={e.type} value={e.type}>{e.label}</option>)}</select></Field>
        </FormGrid>
        <div className="fld-label">Condições (todas precisam bater)</div>
        {conds.map((c, i) => (
          <div key={i} className="row nowrap">
            <select className="input" value={c.field} onChange={(e) => setConds(conds.map((x, j) => (j === i ? { ...x, field: e.target.value } : x)))}>
              <option value="">campo…</option>{fields.map((f) => <option key={f} value={f}>{f.replace("payload.", "")}</option>)}
            </select>
            <select className="input" style={{ width: 150 }} value={c.op} onChange={(e) => setConds(conds.map((x, j) => (j === i ? { ...x, op: e.target.value } : x)))}>
              <option value="eq">igual a</option><option value="neq">diferente de</option><option value="in">um de (vírgula)</option><option value="contains">contém</option><option value="exists">preenchido</option><option value="gt">maior que</option><option value="lt">menor que</option>
            </select>
            {c.op !== "exists" && <input className="input" value={c.value} onChange={(e) => setConds(conds.map((x, j) => (j === i ? { ...x, value: e.target.value } : x)))} />}
            <button className="btn small" onClick={() => setConds(conds.filter((_, j) => j !== i))}>×</button>
          </div>
        ))}
        <div><button className="btn small" onClick={() => setConds([...conds, { field: fields[0] ?? "", op: "eq", value: "" }])}>+ condição</button></div>

        <div className="fld-label">Ações</div>
        {acts.map((a, i) => (
          <div key={i} className="card compact">
            <FormGrid cols={3}>
              <Field label="Ação"><select className="input" value={a.type} onChange={(e) => upA(i, { type: e.target.value as ActRow["type"] })}><option value="criar_atividade">Criar atividade</option><option value="email">Enviar e-mail</option></select></Field>
              {a.type === "criar_atividade" ? (<>
                <Field label="Título" span={2} hint="Pode usar {{payload.cnjNumber}}"><input className="input" value={a.title} onChange={(e) => upA(i, { title: e.target.value })} /></Field>
                <Field label="Tipo"><Combobox value={a.typeKey} onChange={(v) => upA(i, { typeKey: v ?? "" })} options={(cat?.activityTypes ?? []).map((t) => ({ value: t.key, label: t.name }))} /></Field>
                <Field label="Responsável">
                  <Combobox value={a.responsible} allowEmpty={false} onChange={(v) => upA(i, { responsible: v ?? "processo" })} options={[
                    { value: "processo", label: "Responsável do processo" }, { value: "caso", label: "Responsável do caso" }, { value: "fila", label: "Fila (sem responsável)" },
                    ...(cat?.users ?? []).map((u) => ({ value: u.id, label: u.displayName })),
                  ]} />
                </Field>
                <Field label="Prazo interno (dias úteis)"><input className="input" value={a.days} onChange={(e) => upA(i, { days: e.target.value })} /></Field>
              </>) : (<>
                <Field label="Para">
                  <select className="input" value={["responsavel_processo", "responsavel_caso", "controlador"].includes(a.to) ? a.to : "outro"} onChange={(e) => upA(i, { to: e.target.value === "outro" ? "" : e.target.value })}>
                    <option value="responsavel_processo">Responsável do processo</option><option value="responsavel_caso">Responsável do caso</option><option value="controlador">Controlador</option><option value="outro">Outro e-mail</option>
                  </select>
                </Field>
                {!["responsavel_processo", "responsavel_caso", "controlador"].includes(a.to) && <Field label="E-mail"><input className="input" value={a.to} onChange={(e) => upA(i, { to: e.target.value })} /></Field>}
                <Field label="Assunto" span={3}><input className="input" value={a.subject} onChange={(e) => upA(i, { subject: e.target.value })} /></Field>
                <Field label="Mensagem" span={3}><textarea className="input" rows={3} value={a.body} onChange={(e) => upA(i, { body: e.target.value })} /></Field>
              </>)}
            </FormGrid>
            {acts.length > 1 && <button className="btn small danger" style={{ marginTop: 6 }} onClick={() => setActs(acts.filter((_, j) => j !== i))}>Remover ação</button>}
          </div>
        ))}
        <div><button className="btn small" onClick={() => setActs([...acts, { type: "criar_atividade", title: "", typeKey: "", responsible: "processo", days: "5", priority: "media", to: "responsavel_processo", subject: "", body: "" }])}>+ ação</button></div>
        <ErrorBox error={m.error} />
      </div>
    </Modal>
  );
}

function ExecModal({ ruleId, onClose }: { ruleId: string; onClose: () => void }): JSX.Element {
  const { data } = useQuery({ queryKey: ["config-regra-exec", ruleId], queryFn: () => api.get<Array<{ id: string; version: number; eventId: string; status: string; result: unknown; error: string | null; createdAt: string }>>(`/config/regras/${ruleId}/execucoes`) });
  return (
    <Modal title="Execuções" onClose={onClose} wide>
      <DataTable rows={data} rowKey={(e) => e.id} empty="Nenhuma execução."
        columns={[
          { key: "d", label: "Quando", width: 150, render: (e) => fmtDateTime(e.createdAt) },
          { key: "v", label: "Versão", width: 70, render: (e) => `v${e.version}` },
          { key: "s", label: "Resultado", width: 100, render: (e) => <Badge tone={e.status === "ok" ? "ok" : e.status === "error" ? "bad" : "muted"}>{e.status}</Badge> },
          { key: "r", label: "Detalhe", render: (e) => e.error ? <span className="text-bad small">{e.error}</span> : <span className="mono small">{JSON.stringify(e.result)}</span> },
        ]} />
    </Modal>
  );
}

// ================================================================ auditoria

function Audit(): JSX.Element {
  const [f, setF] = useState({ action: "", targetType: "", targetId: "" });
  const [before, setBefore] = useState<string | null>(null);
  const [open, setOpen] = useState<Record<string, unknown> | null>(null);
  const { data, error } = useQuery({
    queryKey: ["auditoria", f, before],
    queryFn: () => api.get<{ items: Array<{ id: string; action: string; targetType: string; targetId: string | null; actorName: string; actorType: string; occurredAt: string; reason: string | null; diff: unknown; ip: string | null; entryHash: string }>; nextBeforeId: string | null }>(`/auditoria${qs({ ...f, beforeId: before ?? undefined, take: 100 })}`),
  });
  const [check, setCheck] = useState<{ ok: boolean; checked: number; brokenAt: string | null } | null>(null);
  const verify = useAct(() => api.get<{ ok: boolean; checked: number; brokenAt: string | null }>("/auditoria/verificar?n=5000"), [], setCheck);
  return (
    <Card title="Trilha de auditoria" actions={<button className="btn small" disabled={verify.isPending} onClick={() => verify.mutate()}>Verificar integridade</button>}>
      {check && (check.ok ? <div className="success">Cadeia íntegra: {check.checked} registros conferidos.</div> : <div className="error">Cadeia quebrada no registro {check.brokenAt}. Investigue imediatamente.</div>)}
      <div className="toolbar">
        <input className="input" placeholder="Ação (ex.: financeiro.)" value={f.action} onChange={(e) => { setF({ ...f, action: e.target.value }); setBefore(null); }} />
        <input className="input" placeholder="Tipo (processo, atividade…)" value={f.targetType} onChange={(e) => { setF({ ...f, targetType: e.target.value }); setBefore(null); }} />
        <input className="input" placeholder="ID do alvo" value={f.targetId} onChange={(e) => { setF({ ...f, targetId: e.target.value }); setBefore(null); }} />
      </div>
      <ErrorBox error={error ?? verify.error} />
      <DataTable rows={data?.items} rowKey={(r) => r.id} onRowClick={(r) => setOpen(r as unknown as Record<string, unknown>)}
        columns={[
          { key: "w", label: "Quando", width: 150, render: (r) => fmtDateTime(r.occurredAt) },
          { key: "u", label: "Quem", render: (r) => r.actorName },
          { key: "a", label: "Ação", render: (r) => <span className="mono small">{r.action}</span> },
          { key: "t", label: "Alvo", render: (r) => <span className="small">{r.targetType} <span className="mono muted">{r.targetId?.slice(0, 8)}</span></span> },
          { key: "r", label: "Motivo", render: (r) => <span className="small">{r.reason ?? ""}</span> },
        ]} />
      {data?.nextBeforeId && <div className="pager"><button className="btn" onClick={() => setBefore(data.nextBeforeId)}>Mais antigos</button></div>}
      {open && <Modal title="Registro de auditoria" onClose={() => setOpen(null)} wide><div className="json">{JSON.stringify(open, null, 2)}</div></Modal>}
    </Card>
  );
}

// ================================================================ aparência

const BRAND_PRESETS = ["#1F3350", "#8B5A2B", "#1F4E79", "#2F5D50", "#6D2E46", "#4B3F72", "#A0522D", "#1B1B1B", "#8C6D1F"];
const SIDE_PRESETS = ["#18222F", "#221A14", "#0F172A", "#1E2A24", "#2A1520", "#FFFFFF", "#F3EEE6"];

function Appearance(): JSX.Element {
  const { data: current } = useQuery({ queryKey: ["tema"], queryFn: () => api.get<OfficeTheme>("/config/tema") });
  const [t, setT] = useState<OfficeTheme | null>(null);
  const v = t ?? current ?? {};
  const eff = { ...DEFAULT_THEME, ...v };
  const up = (p: Partial<OfficeTheme>) => { const n = { ...v, ...p }; setT(n); applyTheme(n); };
  const save = useAct(() => api.put("/config/tema", v), [["tema"]], () => setT(null), "Aparência salva para todo o escritório");
  const reset = useAct(() => api.put("/config/tema", {}), [["tema"]], () => { setT(null); applyTheme({}); }, "Aparência padrão restaurada");
  return (
    <div className="stack">
    <BrandSettings />
    <div className="grid cols-2">
      <Card title="Cores e fonte do escritório">
        <div className="stack">
          <div className="muted small">Vale para todos os usuários. Cada pessoa ainda pode usar o modo escuro (ícone da lua no topo).</div>
          <Field label="Cor principal (botões, destaques, gráficos)">
            <div className="row">
              <div className="swatches">{BRAND_PRESETS.map((c) => <span key={c} className="swatch" style={{ background: c, outline: eff.brand === c ? "2px solid var(--text)" : undefined }} onClick={() => up({ brand: c })} />)}</div>
              <input type="color" className="input" style={{ width: 52, height: 34, padding: 2 }} value={eff.brand} onChange={(e) => up({ brand: e.target.value })} />
            </div>
          </Field>
          <Field label="Menu lateral">
            <div className="row">
              <div className="swatches">{SIDE_PRESETS.map((c) => <span key={c} className="swatch" style={{ background: c, outline: eff.sidebar === c ? "2px solid var(--text)" : undefined }} onClick={() => up({ sidebar: c })} />)}</div>
              <input type="color" className="input" style={{ width: 52, height: 34, padding: 2 }} value={eff.sidebar} onChange={(e) => up({ sidebar: e.target.value })} />
            </div>
          </Field>
          <Field label="Fundo das telas">
            <input type="color" className="input" style={{ width: 52, height: 34, padding: 2 }} value={eff.background} onChange={(e) => up({ background: e.target.value })} />
          </Field>
          <Field label={`Cantos arredondados: ${eff.radius}px`}>
            <input type="range" min={0} max={16} value={eff.radius} onChange={(e) => up({ radius: Number(e.target.value) })} />
          </Field>
          <Field label="Fonte">
            <select className="input" value={v.font ?? "classica"} onChange={(e) => up({ font: e.target.value as OfficeFont })}>
              {(Object.keys(FONTS) as OfficeFont[]).map((k) => <option key={k} value={k}>{FONTS[k].label}</option>)}
            </select>
          </Field>
          <Field label="Nome do escritório (menu, aba do navegador, e-mails)">
            <input className="input" maxLength={64} value={v.officeName ?? ""} onChange={(e) => up({ officeName: e.target.value || undefined })} placeholder="Ex.: Victória Gonçalves" />
          </Field>
          <div className="row">
            <button className="btn primary" disabled={!t || save.isPending} onClick={() => save.mutate()}>Salvar para o escritório</button>
            {t && <button className="btn" onClick={() => { setT(null); applyTheme(current); }}>Descartar</button>}
            <span className="spacer" />
            <button className="btn" onClick={() => reset.mutate()}>Voltar ao padrão</button>
          </div>
          <ErrorBox error={save.error ?? reset.error} />
        </div>
      </Card>
      <Card title="Prévia">
        <div className="theme-preview" style={{ borderRadius: eff.radius }}>
          <div style={{ background: eff.sidebar, padding: 10 }}>
            <div style={{ width: 26, height: 26, borderRadius: 7, background: eff.brand, marginBottom: 12 }} />
            {[0, 1, 2, 3].map((i) => <div key={i} style={{ height: 6, borderRadius: 3, margin: "8px 0", background: i === 1 ? eff.brand : "rgba(128,128,128,.4)" }} />)}
          </div>
          <div style={{ background: eff.background, padding: 12 }}>
            <div style={{ height: 10, width: "40%", borderRadius: 5, background: "rgba(0,0,0,.25)", marginBottom: 10 }} />
            <div style={{ background: "#fff", borderRadius: eff.radius, padding: 10, border: "1px solid rgba(0,0,0,.08)" }}>
              <div style={{ height: 8, width: "60%", borderRadius: 4, background: "rgba(0,0,0,.15)", marginBottom: 8 }} />
              <span style={{ display: "inline-block", padding: "4px 10px", borderRadius: 6, background: eff.brand, color: "#fff", fontSize: 11 }}>Botão</span>
            </div>
          </div>
        </div>
        <div className="muted small pad">A prévia também é aplicada na tela inteira enquanto você edita. Só vale para os outros depois de salvar.</div>
      </Card>
    </div>
    </div>
  );
}

// ============================================================ prazos e IA

function DeadlineSettings(): JSX.Element {
  const { data } = useQuery({ queryKey: ["config-prazos"], queryFn: () => api.get<{ internoDiasAntes: number; recesso?: boolean }>("/config/prazos") });
  const [n, setN] = useState<string>("");
  const save = useAct((recesso?: boolean) => api.put("/config/prazos", { internoDiasAntes: n === "" ? data?.internoDiasAntes ?? 3 : Number(n), recesso: recesso ?? data?.recesso ?? true }), [["config-prazos"], ["comunicacao-painel"]], () => setN(""));
  const value = n === "" ? String(data?.internoDiasAntes ?? 3) : n;
  return (
    <Card title="Prazos">
      <div className="row nowrap">
        <span>Prazo interno padrão = prazo fatal menos</span>
        <input className="input" type="number" min={0} max={30} style={{ width: 70 }} value={value} onChange={(e) => setN(e.target.value)} />
        <span>dias úteis</span>
        <button className="btn small primary" disabled={save.isPending || n === ""} onClick={() => save.mutate(undefined)}>Salvar</button>
      </div>
      <label className="row nowrap" style={{ marginTop: 8 }}>
        <input type="checkbox" checked={data?.recesso !== false} disabled={save.isPending} onChange={(e) => save.mutate(e.target.checked)} />
        Suspender a contagem no <b>recesso forense</b> (20/12 a 20/01, CPC art. 220)
      </label>
      <div className="muted small">Suspensões de um tribunal ou vara específicos: cadastre abaixo como feriado "do órgão" com o nome do órgão igual ao da intimação.</div>
      <div className="muted small pad">Usado na análise de intimações. A data planejada é escolhida no mapa de carga da equipe.</div>
      <ErrorBox error={save.error} />
    </Card>
  );
}

export function AiSettings(): JSX.Element {
  const { data } = useQuery({ queryKey: ["config-ia"], queryFn: () => api.get<{ enabled: boolean; keyConfigured: boolean; model: string }>("/config/ia") });
  const save = useAct((enabled: boolean) => api.put("/config/ia", { enabled }), [["config-ia"], ["comunicacao-painel"]]);
  const { can } = useLookups();
  if (!data) return <Card title="Resumo de intimações por IA (beta)"><Loading /></Card>;
  return (
    <>
    <Card title={<>IA <Badge tone="info">beta</Badge></>}>
      <div className="stack gap-8">
        <label className="row nowrap">
          <input type="checkbox" checked={data.enabled} disabled={!data.keyConfigured || save.isPending} onChange={(e) => save.mutate(e.target.checked)} />
          Permitir o uso da IA (resumo da intimação, resumo do processo e rascunho de andamento para o cliente)
        </label>
        <div className="muted small">
          Só quando alguém pede, o texto usado (intimação, movimentações do processo ou o andamento escolhido) é enviado ao provedor de IA
          (Anthropic, modelo {data.model}). Tudo o que a IA devolve é sugestão: quem decide é a pessoa. Prazos são sempre calculados pelo sistema.
        </div>
        {!data.keyConfigured && <div className="warn-box">Falta a chave <code>ANTHROPIC_API_KEY</code> no AWS Secrets Manager (<code>secrets-set.sh</code>) — nunca no <code>.env</code>.</div>}
        <ErrorBox error={save.error} />
      </div>
    </Card>
    {can("config.gerenciar") && <AiUsagePanel />}
    </>
  );
}

// ============================================================ acesso aos tribunais

interface Portal { tribunal: string; label: string; url: string; sistema?: string | null; orgaoContem?: string | null }

/** Links diretos para o sistema de cada tribunal (painel do advogado), mostrados na intimação. */
function Portals(): JSX.Element {
  const { data } = useQuery({ queryKey: ["config-tribunais"], queryFn: () => api.get<{ portals: Portal[] }>("/config/tribunais") });
  const [rows, setRows] = useState<Portal[] | null>(null);
  const list = rows ?? data?.portals ?? [];
  const save = useAct(() => api.put("/config/tribunais", { portals: list.filter((p) => p.tribunal && p.url) }), [["config-tribunais"], ["comunicacao-painel"]], () => setRows(null));
  const up = (i: number, patch: Partial<Portal>) => setRows(list.map((p, j) => (j === i ? { ...p, ...patch } : p)));
  return (
    <Card title="Acesso direto aos tribunais" actions={<button className="btn small primary" disabled={!rows || save.isPending} onClick={() => save.mutate()}>Salvar</button>}>
      <div className="muted small" style={{ marginBottom: 8 }}>
        Aparecem como botão na intimação do tribunal correspondente (sigla igual à do DJEN: TJBA, TJPE, TRF5…) e levam ao sistema do tribunal, que pede login.
        Quando o tribunal usa mais de um sistema, a escolha é: <b>sistema do processo</b> (campo "Sistema" no cadastro) → <b>termo no nome do órgão</b>
        (ex.: "juizado" → Projudi) → acesso sem termo (geral). Os demais aparecem como botões secundários.
      </div>
      <div className="stack gap-4">
        {list.map((p, i) => (
          <div key={i} className="row nowrap">
            <input className="input" style={{ width: 90 }} placeholder="TJBA" value={p.tribunal} onChange={(e) => up(i, { tribunal: e.target.value.toUpperCase() })} />
            <input className="input" style={{ width: 200 }} placeholder="Nome do botão" value={p.label} onChange={(e) => up(i, { label: e.target.value })} />
            <input className="input" style={{ width: 100 }} placeholder="Sistema" title="PJe, Projudi, eproc, e-SAJ…" value={p.sistema ?? ""} onChange={(e) => up(i, { sistema: e.target.value || null })} />
            <input className="input" style={{ width: 170 }} placeholder="Órgão contém (opcional)" title="Ex.: juizado, turma recursal — separe por vírgula" value={p.orgaoContem ?? ""} onChange={(e) => up(i, { orgaoContem: e.target.value || null })} />
            <input className="input" placeholder="https://…" value={p.url} onChange={(e) => up(i, { url: e.target.value })} />
            <button className="btn ghost small" onClick={() => setRows(list.filter((_, j) => j !== i))}>✕</button>
          </div>
        ))}
        <div><button className="btn small" onClick={() => setRows([...list, { tribunal: "", label: "", url: "", sistema: null, orgaoContem: null }])}>+ acesso</button></div>
      </div>
      <ErrorBox error={save.error} />
    </Card>
  );
}

// ============================================================ SharePoint

interface SpConfig {
  cacheDays?: number; tribunalCacheDays?: number; localCache?: { onServer: number; onlySharepoint: number };
  configured: boolean; site: string | null; driveId: string | null; mode: "normal" | "emergencia"; rootFolder: string;
  lastDeltaAt: string | null; lastUploadAt: string | null; lastError: string | null;
  mirror: { kind: "s3" | "local"; label: string; files: number; enabled?: boolean };
  documents: Record<string, number>;
}

function SharePointSettings(): JSX.Element {
  const { data, error } = useQuery({ queryKey: ["config-sharepoint"], queryFn: () => api.get<SpConfig>("/config/sharepoint") });
  const qc = useQueryClient();
  const inv = [["config-sharepoint"], ["sharepoint-status"], ["sharepoint-pasta"]];
  const [root, setRoot] = useState<string | null>(null);
  const [modeDlg, setModeDlg] = useState(false);
  const [testResult, setTestResult] = useState<string | null>(null);
  const test = useAct(() => api.post<{ drive: { name: string; webUrl: string }; root: { webUrl: string } }>("/config/sharepoint/testar"), [], (r) => setTestResult(`Conectado à biblioteca "${r.drive.name}". Pasta raiz: ${r.root.webUrl}`), false);
  const saveRoot = useAct(() => api.put("/config/sharepoint", { rootFolder: root }), inv, () => setRoot(null));
  const sync = useAct(() => api.post<{ sent: number; mirrored: number; errors: string[] }>("/config/sharepoint/sincronizar"), inv, undefined, "Sincronização executada");
  const [cache, setCache] = useState<string | null>(null);
  const saveCache = useAct(() => api.put("/config/sharepoint", { cacheDays: Number(cache) }), inv, () => setCache(null));
  const toggleMirror = useAct((on: boolean) => api.put("/config/sharepoint", { mirrorEnabled: on }), inv);
  if (error) return <ErrorBox error={error} />;
  if (!data) return <Loading />;
  const emergency = data.mode === "emergencia";
  return (
    <div className="stack gap-16">
      <Card title={<>SharePoint {data.configured ? <Badge tone="ok">configurado</Badge> : <Badge tone="warn">não configurado</Badge>} {emergency && <Badge tone="bad">modo de emergência</Badge>}</>}
        actions={<div className="row nowrap">
          {data.configured && <button className="btn small" disabled={test.isPending} onClick={() => { setTestResult(null); test.mutate(); }}>{test.isPending ? "Testando…" : "Testar conexão"}</button>}
          <button className="btn small" disabled={sync.isPending} onClick={() => sync.mutate()}>Sincronizar agora</button>
          {(data.mirror.enabled || emergency) && <button className={`btn small ${emergency ? "primary" : "danger"}`} onClick={() => setModeDlg(true)}>{emergency ? "Voltar ao modo normal" : "Ativar modo de emergência"}</button>}
        </div>}>
        <ErrorBox error={test.error ?? sync.error ?? saveRoot.error ?? saveCache.error ?? toggleMirror.error} />
        {testResult && <div className="success">{testResult}</div>}
        {!data.configured && (
          <div className="warn-box" style={{ marginBottom: 10 }}>
            Para ligar o SharePoint, cadastre um aplicativo no Microsoft Entra ID (Azure) com a permissão de aplicativo <b>Sites.ReadWrite.All</b>
            (ou <b>Sites.Selected</b> liberada só para o site do escritório) e preencha no <code>apps/api/.env</code> do servidor:
            <pre className="pre small">{`SHAREPOINT_TENANT_ID=...\nSHAREPOINT_CLIENT_ID=...\nSHAREPOINT_CLIENT_SECRET=...\nSHAREPOINT_SITE=empresa.sharepoint.com:/sites/Juridico`}</pre>
            O segredo fica só no servidor (em produção, no AWS Secrets Manager). Sem SharePoint, o espelho de segurança continua funcionando.
          </div>
        )}
        <div className="kv-grid">
          <div><span>Site</span><b>{data.site ?? data.driveId ?? "—"}</b></div>
          <div><span>Último envio de documentos</span><b>{fmtDateTime(data.lastUploadAt)}</b></div>
          {data.mirror.enabled && <div><span>Última atualização do espelho</span><b>{fmtDateTime(data.lastDeltaAt)}</b></div>}
          <div><span>Documentos no SharePoint</span><b>{data.documents.ok ?? 0}</b></div>
          <div><span>Aguardando envio</span><b>{data.documents.pendente ?? 0}</b></div>
          <div><span>Com erro</span><b className={(data.documents.erro ?? 0) ? "text-bad" : ""}>{data.documents.erro ?? 0}</b></div>
          {data.localCache && <div><span>Com cópia no servidor (cache)</span><b>{data.localCache.onServer}</b></div>}
          {data.localCache && <div><span>Só no SharePoint</span><b>{data.localCache.onlySharepoint}</b></div>}
          {data.mirror.enabled && <div className="span-2"><span>Espelho de segurança</span><b>{data.mirror.label}</b></div>}
          {data.mirror.enabled && <div><span>Arquivos espelhados do SharePoint</span><b>{data.mirror.files}</b></div>}
        </div>
        {data.lastError && !(!data.mirror.enabled && /espelho/i.test(data.lastError)) && <div className="text-bad small" style={{ marginTop: 8 }}>Último erro: {data.lastError}</div>}
      </Card>
      <Card title="Cópia dos arquivos no servidor">
        <div className="muted small" style={{ marginBottom: 10 }}>
          O SharePoint é o lugar oficial dos arquivos. A cópia no servidor é só um cache: depois que o documento é confirmado no SharePoint,
          ela é apagada após o prazo abaixo (a limpeza roda todo dia às 3h30). Ao abrir um documento antigo, o VG APP busca no SharePoint e o prazo recomeça.
          Documento que não subiu para o SharePoint nunca é apagado do servidor. Teor baixado dos tribunais: {data.tribunalCacheDays ?? 180} dias.
        </div>
        <div className="row nowrap">
          <Field label="Manter no servidor por (dias)" hint="0 = apagar logo depois de confirmar no SharePoint"><input className="input" type="number" min={0} max={3650} value={cache ?? String(data.cacheDays ?? 30)} onChange={(e) => setCache(e.target.value)} /></Field>
          <button className="btn small primary" disabled={cache === null || cache === "" || saveCache.isPending} onClick={() => saveCache.mutate()}>Salvar</button>
        </div>
      </Card>
      <Card title={<>Cópia de segurança (espelho) {data.mirror.enabled ? <Badge tone="ok">ligada</Badge> : <Badge tone="muted">desligada</Badge>}</>}
        actions={<button className="btn small" disabled={toggleMirror.isPending || emergency} onClick={() => void confirmar(data.mirror.enabled
          ? "Desligar a cópia de segurança? A cópia antiga é apagada na próxima limpeza e o modo de emergência fica indisponível."
          : "Ligar a cópia de segurança? O SharePoint passa a ser copiado (a primeira cópia pode demorar).").then((ok) => ok && toggleMirror.mutate(!data.mirror.enabled))}>{data.mirror.enabled ? "Desligar" : "Ligar"}</button>}>
        <div className="muted small">
          Cópia dos arquivos do SharePoint fora dele ({data.mirror.label}), atualizada a cada hora. É o que permite o modo de emergência
          (ler as pastas se o SharePoint ficar fora do ar). Desligada: o SharePoint é a única cópia dos arquivos — mantenha nele versões, lixeira e uma política de retenção.
        </div>
      </Card>
      <Card title="Estrutura das pastas">
        <div className="row nowrap">
          <Field label="Pasta raiz no SharePoint"><input className="input" value={root ?? data.rootFolder} onChange={(e) => setRoot(e.target.value)} /></Field>
          <button className="btn small primary" disabled={root === null || root.trim().length < 2 || saveRoot.isPending} onClick={() => void confirmar("Trocar a pasta raiz? As pastas de clientes, casos e processos passam a ser criadas na nova raiz (as antigas não são apagadas).").then((ok) => ok && saveRoot.mutate())}>Salvar</button>
        </div>
        <pre className="pre small">{`${data.rootFolder}/Clientes/NOME DO CLIENTE (1234)/Caso 12 - Divórcio/Processo 0001234-43.2026.8.17.3401`}</pre>
        <div className="muted small">
          A pasta é criada quando alguém clica em "Criar pasta" ou quando o primeiro documento do contexto é enviado. Arquivos colocados direto no SharePoint aparecem
          na aba Documentos de cada cliente, caso ou processo.
        </div>
      </Card>
      {modeDlg && (
        <ReasonDialog
          title={emergency ? "Voltar ao modo normal" : "Ativar modo de emergência"}
          label={emergency ? "Motivo (ex.: SharePoint normalizado)" : "Motivo (ex.: SharePoint fora do ar, conta bloqueada)"}
          confirm={emergency ? "Voltar ao normal" : "Ativar emergência"}
          onClose={() => setModeDlg(false)}
          onSubmit={async (reason) => { await api.put("/config/sharepoint", { mode: emergency ? "normal" : "emergencia", reason }); await Promise.all(inv.map((k) => qc.invalidateQueries({ queryKey: k }))); }}>
          <div className="muted small" style={{ marginBottom: 8 }}>
            {emergency
              ? "Os documentos guardados durante a emergência serão enviados ao SharePoint na próxima sincronização."
              : "Todos passam a ver as pastas pela cópia do espelho (somente leitura). Documentos novos continuam sendo aceitos e sobem ao SharePoint quando o modo normal voltar."}
          </div>
        </ReasonDialog>
      )}
    </div>
  );
}

// ============================================================ segurança

function SecuritySettings(): JSX.Element {
  const { data: cat } = useCatalog();
  const { data } = useQuery({ queryKey: ["config-seguranca"], queryFn: () => api.get<{ mfaPapeis: string[]; mfaTodos: boolean; mfaTodosForcado?: boolean }>("/config/seguranca") });
  const [sel, setSel] = useState<string[] | null>(null);
  const [todos, setTodos] = useState<boolean | null>(null);
  const cur = sel ?? data?.mfaPapeis ?? [];
  const all = todos ?? data?.mfaTodos ?? false;
  const dirty = sel !== null || todos !== null;
  const save = useAct(() => api.put("/config/seguranca", { mfaPapeis: cur, mfaTodos: all }), [["config-seguranca"]], () => { setSel(null); setTodos(null); });
  return (
    <Card title="Verificação em duas etapas (2FA) obrigatória" actions={<button className="btn small primary" disabled={!dirty || save.isPending} onClick={() => void confirmar("Quem precisar e ainda não ativou o 2FA só poderá usar o sistema depois de ativar em Minha conta. Confirmar?").then((ok) => ok && save.mutate())}>Salvar</button>}>
      <label className="check" style={{ marginBottom: 8 }}>
        <input type="checkbox" checked={all} disabled={data?.mfaTodosForcado} onChange={(e) => setTodos(e.target.checked)} /> <b>Todos os usuários</b>
        {data?.mfaTodosForcado && <span className="muted small"> — exigido pelo servidor (produção)</span>}
      </label>
      {!all && <>
        <div className="muted small" style={{ marginBottom: 8 }}>Ou marque os papéis que só entram com 2FA ativo. Recomendado: Administrador, Sócio, Financeiro e Controlador.</div>
        <div className="chips">
          {(cat?.roles ?? []).map((r) => (
            <button key={r.key} type="button" className={`chip ${cur.includes(r.key) ? "on" : ""}`} onClick={() => setSel(cur.includes(r.key) ? cur.filter((x) => x !== r.key) : [...cur, r.key])}>{r.name}</button>
          ))}
        </div>
      </>}
      <ErrorBox error={save.error} />
    </Card>
  );
}
